import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useAccountDormantModal from 'components/accountStatus/hooks/useAccountDormantModal';
import useGoogleAnalytics from 'utils/hooks/useGoogleAnalytics';
import useIsPrivateRoute from 'components/routes/hocs/useIsPrivateRoute';
import { getTheme } from 'selectors/darkMode';
import { toggleTheme } from 'actions/isDarkModeEnabled';
import AppRoutes from 'components/routes';
import ModalRoute from 'components/shared/modals/generic/containers/ModalRoot';
import GlobalRedirect from 'components/shared/generic/GlobalRedirect';
import HeaderContainer from 'components/shared/layout/header/containers/HeaderContainer';
import AuthorizedHeader from 'components/shared/layout/authorizedHeader';
import HelpButton from 'components/shared/layout/helpButton/HelpButton';
import AccountStatusModal from 'components/accountStatus/AccountStatusModal';
import useGenericErrorModal from 'utils/hooks/useGenericErrorsModal';
import { usePrevious } from 'lib/utils';
import { useSetSentryUser } from '../../utils/sentry';
import { hideModal, showModal } from 'actions/modal';
import { ACCOUNT_BLOCKED_MODAL } from 'constants/shared/modalTypes';
import { useLocation } from 'react-router-dom';
import { initializeCustomerIO } from '../../utils/customerIO';
import { AccountStatus } from 'lib/type';
import { useGetAccountQuery } from 'lib/services';

function App() {
    const dispatch = useDispatch();
    const isPrivateRoute = useIsPrivateRoute();
    const isDarkModeEnabled = useSelector(getTheme);
    const { data: account } = useGetAccountQuery();
    const isBlocked =
        account?.status === AccountStatus.BLOCKED ||
        account?.status === AccountStatus.SUSPENDED;
    const prevBlocked = usePrevious(isBlocked);
    const [showDormantModal, handleClose] = useAccountDormantModal();
    const location = useLocation();
    useGoogleAnalytics();

    useSetSentryUser();

    const path = location.pathname;

    useGenericErrorModal();

    const handleThemeCheck = useCallback(() => {
        const item =
            window.localStorage.getItem('isDarkModeEnabled') === 'true';

        dispatch(toggleTheme(item));
    }, [dispatch]);

    useEffect(() => {
        initializeCustomerIO();
        handleThemeCheck();
    }, [handleThemeCheck]);

    useEffect(() => {
        if (isBlocked && !prevBlocked) {
            dispatch(
                showModal({
                    type: ACCOUNT_BLOCKED_MODAL,
                    props: {
                        closeModal: () => dispatch(hideModal()),
                    },
                }),
            );
        }
    }, [prevBlocked, isBlocked, dispatch]);

    const renderHeader = () => {
        if (isPrivateRoute) {
            return <AuthorizedHeader />;
        }

        const isPublicLanding = path === '/auth' || path === '/app' || path.startsWith('/app/');

        if (!isPublicLanding) {
            return <HeaderContainer />;
        }

        return null;
    };

    return (
        <div className="app">
            <div id="page-container">
                {renderHeader()}
                {showDormantModal && (
                    <AccountStatusModal handleClose={handleClose} />
                )}
                <HelpButton />
                <div id="page-routes">
                    <div
                        className={`page-body ${
                            isPrivateRoute ? 'authed' : ''
                        } ${isDarkModeEnabled ? 'dark-mode' : ''}`}
                    >
                        <AppRoutes />
                        <ModalRoute />
                        <GlobalRedirect />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
